import React from 'react';
import { Link } from 'react-router-dom';
import { Heart, Trash2, ShoppingCart, ArrowLeft } from 'lucide-react';
import { useWishlist } from '../contexts/WishlistContext';
import { useCart } from '../contexts/CartContext';

const WishlistPage: React.FC = () => {
  const { items, removeItem, clearWishlist } = useWishlist();
  const { addItem } = useCart();

  const handleAddToCart = (item: any) => {
    addItem({
      id: item.id,
      name: item.name,
      price: item.price,
      image: item.image
    });
  };

  if (items.length === 0) {
    return (
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12 bg-white dark:bg-gray-900 min-h-screen transition-colors duration-300">
        <div className="text-center">
          <Heart className="mx-auto h-24 w-24 text-gray-400 dark:text-gray-600 mb-6" />
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">قائمة الأمنيات فارغة</h2>
          <p className="text-gray-600 dark:text-gray-400 mb-8">
            لم تقم بإضافة أي منتجات إلى قائمة الأمنيات بعد
          </p>
          <Link
            to="/"
            className="btn-primary inline-flex items-center space-x-2"
          >
            <ArrowLeft className="h-5 w-5" />
            <span>العودة للتسوق</span>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 bg-white dark:bg-gray-900 min-h-screen transition-colors duration-300">
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">قائمة الأمنيات</h1>
            <p className="text-gray-600 dark:text-gray-400 mt-2">
              لديك {items.length} منتج في قائمة الأمنيات
            </p>
          </div>
          <button
            onClick={clearWishlist}
            className="text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300 font-medium transition-colors"
          >
            مسح القائمة
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
        {items.map((item) => (
          <div
            key={item.id}
            className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden hover:shadow-md transition-all duration-300"
          >
            <Link to={`/product/${item.id}`}>
              <div className="aspect-w-1 aspect-h-1 w-full overflow-hidden bg-gray-200 dark:bg-gray-700">
                <img
                  src={item.image}
                  alt={item.name}
                  className="h-48 w-full object-cover hover:scale-105 transition-transform duration-200"
                  onError={(e) => {
                    e.currentTarget.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTAwIiBoZWlnaHQ9IjEwMCIgdmlld0JveD0iMCAwIDEwMCAxMDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2Zz4KPHJlY3Qgd2lkdGg9IjEwMCIgaGVpZ2h0PSIxMDAiIGZpbGw9IiNCM0IzQjMiLz4KPHBhdGggZD0iTTMwIDMwSDcwVjcwSDMwVjMwWiIgZmlsbD0iI0QxRDVEQiIvPgo8cGF0aCBkPSJNNDAgNDBIMjBWNjBIMjBWNDBaIiBmaWxsPSIjQzdDQ0Q3Ii8+Cjwvc3ZnPgo=';
                  }}
                />
              </div>
            </Link>
            
            <div className="p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-gray-500 dark:text-gray-400 uppercase tracking-wide transition-colors duration-300">
                  {item.category}
                </span>
                <button
                  onClick={() => removeItem(item.id)}
                  className="text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 transition-colors"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
              
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2 line-clamp-2 transition-colors duration-300">
                <Link to={`/product/${item.id}`} className="hover:text-primary-600 dark:hover:text-primary-400">
                  {item.name}
                </Link>
              </h3>
              
              <div className="flex items-center justify-between">
                <span className="text-2xl font-bold text-primary-600 dark:text-primary-400 transition-colors duration-300">
                  ₪{item.price}
                </span>
                
                <div className="flex space-x-2">
                  <button
                    onClick={() => handleAddToCart(item)}
                    className="btn-primary flex items-center space-x-2 py-2 px-3 text-sm"
                  >
                    <ShoppingCart className="h-4 w-4" />
                    <span>إضافة للسلة</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="mt-8 text-center">
        <Link
          to="/"
          className="btn-secondary inline-flex items-center space-x-2"
        >
          <ArrowLeft className="h-5 w-5" />
          <span>متابعة التسوق</span>
        </Link>
      </div>
    </div>
  );
};

export default WishlistPage;
